import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Tag, Typography } from 'antd';
import { confirmation, ProjectsConstructorCard, useAppFindFormik } from '@/components';
import { ProjectDto, CreateSheetDto, SheetDto, SheetTableDto } from '@/core';
import { useDeleteSheetMutation, useUpdateSheetMutation } from '@/redux';
import { TableTypesEnum } from '../Projects.Constructor.Tables/hooks';
import { FormValueType } from './hooks';
import { SheetForm } from './SheetForm';
import {
  findEpicNameById,
  findTableNameById,
  filterTablesByType,
  validateMainTables,
  getIsTableMain,
} from './helpers';
import classes from './SheetCard.module.scss';

type CardProps = SheetDto & {
  onClickMenuEdit: () => void;
  isEdit: boolean;
  stopEditCard: (id: string) => void;
  projectData: ProjectDto;
};

export const SheetCard = (props: CardProps) => {
  const { name, tables, epics, isEdit, id, onClickMenuEdit, stopEditCard, projectData } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const [updateSheet] = useUpdateSheetMutation();
  const [deleteSheet] = useDeleteSheetMutation();
  const initialValue: FormValueType = {
    name,
    tables: tables.map((table) => table.tableId),
    epics,
  };
  const form = useAppFindFormik(name);
  const [pickedTables, setPickedTables] = useState<string[] | undefined>(initialValue.tables);
  const [mainTableValidationError, setMainTableValidationError] = useState('');
  const mainTables = filterTablesByType(projectData, TableTypesEnum.Main, pickedTables);
  const summaryTables = filterTablesByType(projectData, TableTypesEnum.Summary, pickedTables);

  const clearForm = () => {
    setPickedTables(undefined);
    form.current?.setValues({ name: '', tables: undefined, epics: undefined });
    setMainTableValidationError('');
  };

  const stopEdit = () => {
    setPickedTables(initialValue.tables);
    setMainTableValidationError('');
    stopEditCard(id);
  };

  const submitUpdate = (sheetData: CreateSheetDto) => {
    const validationError = validateMainTables(sheetData.tables, projectData);

    if (validationError) {
      setMainTableValidationError(validationError);
      return;
    }

    updateSheet({
      sheetData: {
        ...sheetData,
        tables: pickedTables?.map((tableId, index) => ({ tableId, sequence: index }))!,
      },
      id,
    })
      .unwrap()
      .then(() => stopEditCard(id));
  };
  const onClickMenuDelete = () =>
    confirmation.show({
      title: t('addSheets.delete.title'),
      body: t('addSheets.delete.body'),
      isConfirmButtonDanger: true,
      onConfirm: () => deleteSheet({ id }),
      confirm: 'Delete',
    });

  const handlePickedTables = (tables: string[]) => {
    setPickedTables(tables);
    setMainTableValidationError('');
  };

  const validateCard = (epics: string[], tables: SheetTableDto[]) =>
    !epics.length || !tables.filter((table) => getIsTableMain(table.tableId, projectData)).length;

  return (
    <ProjectsConstructorCard
      isEdit={isEdit}
      onClickMenuDelete={onClickMenuDelete}
      onClickMenuEdit={onClickMenuEdit}
      invalidCard={validateCard(epics, tables)}
      editForm={
        <SheetForm
          handleSubmit={submitUpdate}
          mainTableValidationError={mainTableValidationError}
          projectData={projectData}
          formUid={name}
          initialValue={initialValue}
          pickedTables={pickedTables}
          setPickedTables={handlePickedTables}
          clearForm={clearForm}
          isEditForm
          stopEditCard={stopEdit}
          mainTables={mainTables}
          summaryTables={summaryTables}
          editedSheetId={id}
        />
      }
    >
      <Col xs={8} md={6}>
        <Typography.Paragraph className="pe-3">{name}</Typography.Paragraph>
      </Col>
      <Col xs={8} md={9}>
        <div className={classes.tagsWrapper}>
          {pickedTables?.map((table: string) => (
            <Tag key={table}>
              <Typography.Text ellipsis>{findTableNameById(table, projectData)}</Typography.Text>
            </Tag>
          ))}
        </div>
      </Col>
      <Col xs={8} md={9}>
        <div className={classes.tagsWrapper}>
          {epics.map((epic: string) => (
            <Tag key={epic}>
              <Typography.Text ellipsis>{findEpicNameById(epic, projectData)}</Typography.Text>
            </Tag>
          ))}
        </div>
      </Col>
    </ProjectsConstructorCard>
  );
};
